/**
 * Logs (`/console`) — de live logstroom met filterknoppen.
 * Endpoint: /api/admin/logs/recent?limit=300, ververst elke 5 s.
 * (De bundle houdt daarnaast een SSE-stream open; hier volstaat pollen,
 *  dezelfde regels en dezelfde volgorde.)
 */
import { useMemo, useState } from 'react'
import { ErrorNote, Loading, Panel, Seg, Tag } from '../components/ui'
import { useApi, usePoll } from '../lib/useApi'

interface LogRow {
  __seq: number
  timestamp: string
  level: string
  event: string
  method?: string
  path?: string
  status?: number
  duration_ms?: number
  [k: string]: unknown
}

const LEVELS = [
  { value: 'all', label: 'All' },
  { value: 'info', label: 'info' },
  { value: 'warning', label: 'warning' },
  { value: 'error', label: 'error' },
]
const SIZES = [100, 300, 600, 1000]
const QUICK: { label: string; match: RegExp }[] = [
  { label: 'errors', match: /error|critical|exception|traceback/i },
  { label: 'posting bot', match: /post|publish|vault|queue|scheduler/i },
  { label: 'F2F', match: /f2f/i },
  { label: 'FC', match: /fc_|fancentro/i },
  { label: 'scrapers', match: /scrape|scraper|stats|followers/i },
]
const LEVEL_COLOR: Record<string, string> = {
  info: 'var(--acc2)', warning: 'var(--warn)', error: 'var(--bad)',
  critical: 'var(--bad)', debug: 'var(--mut2)',
}

export default function Logs() {
  const [limit, setLimit] = useState(300)
  const [level, setLevel] = useState('all')
  const [search, setSearch] = useState('')
  const [quick, setQuick] = useState<string | null>(null)
  const [autoScroll, setAutoScroll] = useState(true)

  const q = useApi<{ data: LogRow[] }>(`/api/admin/logs/recent?limit=${limit}`, [limit])
  usePoll(5000, q.refetch, autoScroll)

  const rows = useMemo(() => {
    let list = q.data?.data ?? []
    if (level !== 'all') list = list.filter((r) => (r.level || '').toLowerCase() === level)
    const rx = QUICK.find((x) => x.label === quick)?.match
    if (rx) list = list.filter((r) => rx.test(JSON.stringify(r)))
    const needle = search.trim().toLowerCase()
    if (needle) list = list.filter((r) => JSON.stringify(r).toLowerCase().includes(needle))
    return list
  }, [q.data, level, quick, search])

  /** Alles behalve de vaste velden als leesbare staart achter de regel. */
  function detail(r: LogRow): string {
    if (r.method || r.path) {
      return `${r.method ?? ''} ${r.path ?? ''}${r.status ? ` · ${r.status}` : ''}` +
             `${r.duration_ms ? ` · ${r.duration_ms}ms` : ''}`
    }
    const skip = new Set(['__seq', 'timestamp', 'level', 'event'])
    return Object.entries(r)
      .filter(([k]) => !skip.has(k))
      .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
      .join(' ')
      .slice(0, 300)
  }

  return (
    <>
      <div className="filters">
        <select className="select" value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}>
          {SIZES.map((n) => <option key={n} value={n}>{n} lines</option>)}
        </select>
        <Seg options={LEVELS} value={level} onChange={setLevel} />
        <input className="inp" style={{ maxWidth: 320 }}
               placeholder="Search (event, message, model, fan id…)"
               value={search} onChange={(e) => setSearch(e.target.value)} />
        <label className="chkrow">
          <span className={autoScroll ? 'chk on' : 'chk'} onClick={() => setAutoScroll((v) => !v)}>
            {autoScroll ? '✓' : ''}
          </span>
          Auto-scroll
        </label>
        <div className="spacer" style={{ flex: 1 }} />
        <Tag kind={autoScroll ? 'ok' : 'mut'}>
          <span className={autoScroll ? 'dotst on-live' : 'dotst off-live'} />
          {autoScroll ? 'Connected' : 'Paused'}
        </Tag>
      </div>

      <Panel
        title="Logs"
        desc={`${rows.length} lines`}
        flush
        right={
          <div className="row">
            {QUICK.map((f) => (
              <button
                key={f.label}
                className={quick === f.label ? 'btn sm' : 'btn ghost sm'}
                type="button"
                onClick={() => setQuick(quick === f.label ? null : f.label)}
              >
                {f.label}
              </button>
            ))}
            <button className="btn ghost sm" type="button"
                    onClick={() => { setQuick(null); setLevel('all'); setSearch('') }}>
              Reset filters
            </button>
          </div>
        }
      >
        {q.loading && !q.data ? <Loading /> : q.error ? <ErrorNote error={q.error} /> : (
          // 22-10-2026 (Jordy): op hoge schermen tot onderaan; nooit lager dan 640px.
          <div style={{ maxHeight: 'max(640px, calc(100vh / var(--dz, 1) - 260px))', overflow: 'auto' }}>
            {rows.map((r) => (
              <div className="logline" key={r.__seq}>
                <span className="mut2">{String(r.timestamp).replace('T', ' ').slice(0, 19)}</span>
                <span className="lvl" style={{ color: LEVEL_COLOR[r.level] || 'var(--mut)' }}>
                  {(r.level || 'info').toUpperCase()}
                </span>
                <span className="mut">
                  <b style={{ color: 'var(--txt)' }}>{r.event}</b> {detail(r)}
                </span>
              </div>
            ))}
            {!rows.length ? <div className="empty">No lines match the filter</div> : null}
          </div>
        )}
      </Panel>
    </>
  )
}
